import { useCallback } from "react";
import { Article } from "../types/Article";
import { PagedResults } from "../types/PagedResults";
import useApiClient from "./ApiClient";
import { HttpError } from "../types/Error";

const useArticlesService = () => {
  const { get, post, postFormData, putFormData, delete: del } = useApiClient();

  const getArticle = useCallback(
    async (id: string): Promise<Article | HttpError> => {
      try {
        const result = await get<Article>(`/admin/articles/${id}`);
        return result;
      } catch (error) {
        console.error("Error fetching article", error);
        throw error;
      }
    },
    [get]
  );

  const searchArticles = useCallback(
    async (
      searchTerm: string,
      page: number = 1,
      includeArchive: boolean = false,
      itemsPerPage: number = 50
    ): Promise<PagedResults<Article>> => {
      try {
        const results = await post<PagedResults<Article>>(
          "/admin/articles/search",
          { searchTerm, includeArchive, page, itemsPerPage }
        );
        // Check if the response is an error.
        if ((results as HttpError).errorCode) {
          throw (results as HttpError).description;
        }

        return results as PagedResults<Article>;
      } catch (error) {
        console.error("Error searching articles", error);
        throw error;
      }
    },
    [post]
  );

  const createArticle = useCallback(
    async (articleData: FormData): Promise<Article | HttpError> => {
      try {
        const result = await postFormData<Article>(
          "/admin/articles",
          articleData
        );
        return result;
      } catch (error) {
        console.error("Error creating article", error);
        throw error;
      }
    },
    [postFormData]
  );

  const updateArticle = useCallback(
    async (id: String, articleData: FormData): Promise<Article | HttpError> => {
      try {
        const result = await putFormData<Article>(
          "/admin/articles/" + id,
          articleData
        );
        return result;
      } catch (error) {
        console.error("Error updating article", error);
        throw error;
      }
    },
    [putFormData]
  );

  const deleteArticle = useCallback(
    async (id: String): Promise<boolean | HttpError> => {
      try {
        await del("/admin/articles/" + id);
        return true;
      } catch (error) {
        console.error("Error deleting article", error);
        throw error;
      }
    },
    [del]
  );

  return {
    searchArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    getArticle,
  };
};

export default useArticlesService;
